import React, { useRef, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { UploadCloud, Loader2, X, ImageIcon } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ImageUpload({ value, onChange, disabled = false, testid = "image-upload" }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image file."); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads/image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const fullUrl = `${BACKEND_URL}${data.url}`;
      onChange(fullUrl);
      toast.success("Image uploaded.");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    if (disabled) return;
    const file = e.dataTransfer.files?.[0];
    upload(file);
  };

  if (value) {
    return (
      <div className="relative overflow-hidden rounded-xl border border-ff-border" data-testid={`${testid}-preview`}>
        <img src={value} alt="Upload preview" className="max-h-72 w-full object-cover" />
        {!disabled && (
          <button type="button" data-testid={`${testid}-remove`} onClick={() => onChange("")}
            className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full bg-white/90 text-ff-red shadow-sm hover:bg-white">
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
    );
  }

  return (
    <div
      data-testid={testid}
      onClick={() => !disabled && inputRef.current?.click()}
      onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={onDrop}
      className={`flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed p-10 text-center transition-colors duration-200 ${dragging ? "border-ff-red bg-[#FEF2F2]" : "border-ff-border bg-[#F3F4F6] hover:border-ff-blue"} ${disabled ? "pointer-events-none opacity-60" : ""}`}
    >
      <input ref={inputRef} type="file" accept="image/*" className="hidden" data-testid={`${testid}-input`}
        onChange={(e) => upload(e.target.files?.[0])} disabled={disabled} />
      {uploading ? (
        <><Loader2 className="mb-3 h-8 w-8 animate-spin text-ff-red" /><span className="text-sm font-semibold text-ff-blue">Uploading…</span></>
      ) : disabled ? (
        <><ImageIcon className="mb-3 h-8 w-8 text-ff-medium" /><span className="text-sm font-semibold text-ff-dark">Image uploads are locked while this campaign is live or under review.</span></>
      ) : (
        <>
          <UploadCloud className="mb-3 h-8 w-8 text-ff-medium" />
          <span className="text-sm font-semibold text-ff-blue">Drag & drop an image, or click to browse</span>
          <span className="mt-1 text-xs text-ff-medium">JPG, PNG, GIF or WebP · up to 8 MB</span>
        </>
      )}
    </div>
  );
}
